import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  Alert, CircularProgress, Stepper, Step, StepLabel,
  Chip, Select, MenuItem, FormControl, InputLabel,
  LinearProgress, Table, TableBody, TableCell, TableRow
} from '@mui/material';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import TuneIcon         from '@mui/icons-material/Tune';
import RadarIcon        from '@mui/icons-material/Radar';
import TimerIcon        from '@mui/icons-material/Timer';
import PlayArrowIcon    from '@mui/icons-material/PlayArrow';
import StopIcon         from '@mui/icons-material/Stop';
import { sitesAPI }     from '../services/api';
import api              from '../services/api';

const DISTANCES  = [1, 3, 5, 8];
const COUNTDOWN  = 10;

export default function Calibration() {
  const [sites,          setSites]          = useState([]);
  const [siteId,         setSiteId]         = useState('');
  const [sensors,        setSensors]        = useState([]);
  const [sensorId,       setSensorId]       = useState('');
  const [step,           setStep]           = useState(0);
  const [measures,       setMeasures]       = useState({});
  const [result,         setResult]         = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [measuring,      setMeasuring]      = useState(null);
  const [countdown,      setCountdown]      = useState(null);
  const [waiting,        setWaiting]        = useState(false);
  const [calibStarted,   setCalibStarted]   = useState(false);
  const [starting,       setStarting]       = useState(false);
  const [calibReady,     setCalibReady]     = useState(false);
  const [polling,        setPolling]        = useState(false);
  const pollingRef = useRef(null);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');
  const countdownRef = useRef(null);

  useEffect(() => {
    sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!siteId) return;
    api.get(`/v1/walkthrough/sensors/${siteId}`)
      .then(r => setSensors(r.data.sensors || []))
      .catch(() => {});
  }, [siteId]);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Démarrer le polling du mode capteur
  const startPolling = () => {
    setPolling(true);
    setCalibReady(false);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/v1/calibration/${sensorId}/mode`);
        if (res.data?.mode === 'calibrating') {
          setCalibReady(true);
          setPolling(false);
          clearInterval(pollingRef.current);
        }
      } catch (e) {}
    }, 5000);
  };

  // Arrêter le polling
  const stopPolling = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setPolling(false);
    setCalibReady(false);
  };

  // Démarrer la session de calibration
  const startCalibration = async () => {
    setStarting(true);
    setError('');
    try {
      await api.post(`/v1/calibration/${sensorId}/start`, {});
      setCalibStarted(true);
      setSuccess('Commande envoyée — en attente activation capteur...');
      startPolling();
    } catch (e) {
      setError('Erreur activation mode calibration');
    } finally {
      setStarting(false);
    }
  };

  // Arrêter la session de calibration
  const stopCalibration = async () => {
    try {
      await api.post(`/v1/calibration/${sensorId}/stop`);
      setCalibStarted(false);
      stopPolling();
      setSuccess('Mode calibration désactivé — capteur repassé en mode normal (5 min)');
    } catch (e) {
      setError('Erreur désactivation calibration');
    }
  };

  // Démarrer le compte à rebours puis mesurer
  const startMeasure = (distance, stepIndex) => {
    setError('');
    setWaiting(true);
    setCountdown(COUNTDOWN);
    let remaining = COUNTDOWN;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        setWaiting(false);
        setCountdown(null);
        doMeasure(distance, stepIndex);
      }
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setWaiting(false);
    setCountdown(null);
  };

  // Mesurer le RSSI via l'API
  const doMeasure = async (distance, stepIndex) => {
    setMeasuring(distance);
    try {
      const res  = await api.get(`/v1/calibration/${sensorId}/measure`);
      const rssi = res.data?.rssi;
      if (rssi) {
        setMeasures(prev => ({ ...prev, [distance]: rssi }));
        if (stepIndex < DISTANCES.length - 1) {
          setTimeout(() => setStep(stepIndex + 1), 400);
        }
      } else {
        setError('Aucune detection recente. Attendez 10 sec et reessayez.');
      }
    } catch (e) {
      setError('Erreur mesure RSSI.');
    } finally {
      setMeasuring(null);
    }
  };

  const computeN = () => {
    const txpower = parseInt(measures[1]) || -59;
    const ns = [];
    DISTANCES.forEach(d => {
      const rssi = parseInt(measures[d]);
      if (rssi && d > 1) {
        const n = (txpower - rssi) / (10 * Math.log10(d));
        ns.push(n);
      }
    });
    if (ns.length === 0) return null;
    const nAvg = ns.reduce((a, b) => a + b, 0) / ns.length;
    const precision = DISTANCES.filter(d => measures[d]).reduce((acc, d) => {
      const rssi = parseInt(measures[d]);
      const dEst = Math.pow(10, (txpower - rssi) / (10 * nAvg));
      return acc + Math.abs(dEst - d);
    }, 0) / DISTANCES.filter(d => measures[d]).length;
    return {
      n: Math.round(nAvg * 100) / 100,
      txpower,
      precision: Math.round(precision * 100) / 100
    };
  };

  const saveCalibration = async () => {
    const r = computeN();
    if (!r) return;
    setLoading(true);
    try {
      await api.put(`/v1/sensors/${sensorId}/calibration`, {
        txpower: r.txpower, n_factor: r.n, precision_m: r.precision
      }).catch(() => {});
      // Arrêter le mode calibration
      await stopCalibration();
      setResult(r);
      setSuccess(`Calibration sauvegardée — n=${r.n}, précision ~${r.precision}m`);
      setStep(4);
    } catch (e) {
      setError('Erreur sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    cancelCountdown();
    setStep(0); setMeasures({}); setResult(null);
    setError(''); setSuccess('');
    if (calibStarted) stopCalibration();
    setCalibStarted(false);
  };

  const progressPct = countdown !== null
    ? Math.round(((COUNTDOWN - countdown) / COUNTDOWN) * 100)
    : 0;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1F4E79" mb={1}>
        Calibration signal
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Calibrez le facteur d'atténuation (n) pour une triangulation précise
      </Typography>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Selection site + capteur */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Boutique</InputLabel>
                <Select value={siteId} label="Boutique"
                  onChange={e => { setSiteId(e.target.value); setSensorId(''); reset(); }}>
                  {sites.map(s => (
                    <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Capteur</InputLabel>
                <Select value={sensorId} label="Capteur"
                  onChange={e => { setSensorId(e.target.value); reset(); }}>
                  {sensors.map(s => (
                    <MenuItem key={s.sensor_id} value={s.sensor_id}>
                      {s.sensor_id} — {s.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {sensorId && !calibStarted && (
                <Button fullWidth variant="contained" size="large"
                  startIcon={starting
                    ? <CircularProgress size={18} color="inherit" />
                    : <PlayArrowIcon />}
                  disabled={starting}
                  onClick={startCalibration}
                  sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' },
                    borderRadius: 2, py: 1.5 }}>
                  {starting ? 'Activation...' : 'Démarrer calibration'}
                </Button>
              )}
              {sensorId && calibStarted && (
                <Button fullWidth variant="outlined" size="large"
                  startIcon={<StopIcon />}
                  onClick={reset}
                  sx={{ color: '#E53935', borderColor: '#E53935',
                    borderRadius: 2, py: 1.5 }}>
                  Arrêter calibration
                </Button>
              )}
            </Grid>
          </Grid>

          {calibStarted && !calibReady && polling && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={<CircularProgress size={18} />}>
              <strong>En attente du capteur...</strong> — Le capteur va passer en mode calibration
              dans moins de <strong>5 minutes</strong>. Préparez votre téléphone.
            </Alert>
          )}
          {calibStarted && calibReady && (
            <Alert severity="success" sx={{ mt: 2 }} icon={<RadarIcon />}>
              <strong>✅ Capteur prêt !</strong> — Mode calibration actif, mesures toutes les <strong>10 secondes</strong>.
              Vous pouvez démarrer les mesures !
            </Alert>
          )}
        </CardContent>
      </Card>

      {sensorId && calibStarted && (
        <>
          <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
            {DISTANCES.map(d => (
              <Step key={d} completed={!!measures[d]}>
                <StepLabel>{d}m</StepLabel>
              </Step>
            ))}
            <Step completed={!!result}>
              <StepLabel>Résultat</StepLabel>
            </Step>
          </Stepper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} mb={3}>
                    Mesures terrain
                  </Typography>

                  {DISTANCES.map((d, i) => {
                    const isActive    = i === step;
                    const isDone      = !!measures[d];
                    const isLocked    = i > step;
                    const isWaiting   = waiting && isActive;
                    const isMeasuring = measuring === d;

                    return (
                      <Box key={d} sx={{ mb: 3, p: 2, borderRadius: 2,
                        bgcolor: isActive ? '#E3F2FD' : (isDone ? '#F1F8E9' : '#FAFAFA'),
                        border: isActive ? '2px solid #1F4E79' : '1px solid #E0E0E0',
                        opacity: isLocked ? 0.4 : 1,
                        transition: 'all 0.3s' }}>

                        <Box sx={{ display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between', mb: isActive ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={`${d}m`} size="small"
                              sx={{ bgcolor: isActive ? '#1F4E79' : (isDone ? '#4CAF50' : '#E0E0E0'),
                                color: (isActive || isDone) ? 'white' : 'text.secondary',
                                fontWeight: 600 }} />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {i === 0 ? 'Placez-vous à 1m du capteur'
                                         : `Reculez à ${d}m du capteur`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {i === 0 ? 'Mesure TxPower de référence'
                                         : 'Restez immobile 10 secondes'}
                              </Typography>
                            </Box>
                          </Box>
                          {isDone && (
                            <Box sx={{ textAlign: 'right' }}>
                              <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                              <Typography variant="caption" display="block"
                                color="#4CAF50" fontWeight={600}>
                                {measures[d]} dBm
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {isActive && !isDone && (
                          <Box>
                            {isWaiting && (
                              <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center',
                                  justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2" color="#1F4E79" fontWeight={600}>
                                    <TimerIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                    Restez immobile — mesure dans {countdown} sec
                                  </Typography>
                                  <Typography variant="caption" color="#1F4E79" fontWeight={700}>
                                    {countdown}s
                                  </Typography>
                                </Box>
                                <LinearProgress variant="determinate"
                                  value={progressPct}
                                  sx={{ height: 10, borderRadius: 5,
                                    bgcolor: '#BBDEFB',
                                    '& .MuiLinearProgress-bar': { bgcolor: '#1F4E79' }
                                  }} />
                              </Box>
                            )}

                            {!isWaiting && !isMeasuring && calibReady && (
                              <Button fullWidth variant="contained" size="large"
                                startIcon={<RadarIcon />}
                                onClick={() => startMeasure(d, i)}
                                sx={{ bgcolor: '#1F4E79',
                                  '&:hover': { bgcolor: '#163a5f' },
                                  borderRadius: 2, py: 1.5 }}>
                                Mesurer à {d}m
                              </Button>
                            )}

                            {!calibReady && !isWaiting && (
                              <Alert severity="info" sx={{ borderRadius: 2 }}>
                                En attente activation capteur...
                              </Alert>
                            )}
                            {isMeasuring && (
                              <Button fullWidth variant="contained" size="large"
                                disabled
                                startIcon={<CircularProgress size={18} color="inherit" />}
                                sx={{ borderRadius: 2, py: 1.5 }}>
                                Lecture RSSI...
                              </Button>
                            )}

                            {isWaiting && (
                              <Button fullWidth variant="outlined" size="small"
                                onClick={cancelCountdown}
                                sx={{ mt: 1, borderRadius: 2,
                                  color: '#E53935', borderColor: '#E53935' }}>
                                Annuler
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                  <Button fullWidth variant="contained" size="large"
                    onClick={saveCalibration}
                    disabled={loading || DISTANCES.filter(d => measures[d]).length < 2}
                    startIcon={loading
                      ? <CircularProgress size={18} color="inherit" />
                      : <TuneIcon />}
                    sx={{ mt: 1, bgcolor: '#4CAF50',
                      '&:hover': { bgcolor: '#388E3C' },
                      '&:disabled': { bgcolor: '#E0E0E0' },
                      borderRadius: 2, py: 1.5 }}>
                    Calculer et sauvegarder
                  </Button>

                  <Button fullWidth variant="outlined" onClick={reset}
                    sx={{ mt: 1, borderRadius: 2 }}>
                    Recommencer
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Résultats */}
            <Grid item xs={12} md={5}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography fontWeight={600} mb={2}>Résultats en temps réel</Typography>
                  {(() => {
                    const r = computeN();
                    if (!r) return (
                      <Typography color="text.secondary" variant="body2">
                        Effectuez au moins 2 mesures pour voir les résultats
                      </Typography>
                    );
                    return (
                      <Box>
                        <Table size="small">
                          <TableBody>
                            {[
                              ['TxPower',           `${r.txpower} dBm`],
                              ['Facteur n',            r.n],
                              ['Précision estimée', `~${r.precision} m`],
                              ['Environnement',
                                r.n < 2.3 ? 'Espace ouvert' :
                                r.n < 2.8 ? 'Boutique standard' :
                                r.n < 3.3 ? 'Obstacles modérés' : 'Dense (métal)'],
                            ].map(([k, v]) => (
                              <TableRow key={k}>
                                <TableCell sx={{ color: 'text.secondary', border: 'none', py: 0.5 }}>{k}</TableCell>
                                <TableCell sx={{ fontWeight: 600, border: 'none', py: 0.5 }}>{v}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Qualité calibration
                          </Typography>
                          <LinearProgress variant="determinate"
                            value={Math.max(0, 100 - r.precision * 20)}
                            sx={{ height: 8, borderRadius: 4, mt: 0.5,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: r.precision < 2 ? '#4CAF50' :
                                         r.precision < 4 ? '#FF9800' : '#F44336'
                              }}} />
                          <Typography variant="caption" color={
                            r.precision < 2 ? '#4CAF50' :
                            r.precision < 4 ? '#FF9800' : '#F44336'}>
                            {r.precision < 2 ? 'Excellente' :
                             r.precision < 4 ? 'Acceptable' : 'À améliorer'}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                mt: 2, bgcolor: '#E3F2FD' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="body2" fontWeight={600} color="#1F4E79" mb={1}>
                    Instructions
                  </Typography>
                  {[
                    'Cliquez "Démarrer calibration"',
                    'Activez le WiFi sur votre téléphone',
                    'Placez-vous à la distance indiquée',
                    'Cliquez "Mesurer" et restez immobile',
                    'Attendez le compte à rebours de 10 sec',
                    'Répétez pour chaque distance',
                    'Cliquez "Calculer" une fois terminé',
                  ].map((t, i) => (
                    <Typography key={i} variant="caption" display="block"
                      color="#1F4E79" mb={0.5}>
                      {i + 1}. {t}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
